/**
 * "He has not touched anything for a while."
 *
 * The rule is a pure function so it can be tested without a clock; the hook is the thin wiring.
 * Ten minutes: long enough that reading a draft never trips it, short enough that a screen left on
 * in the office turns into the wall before a visitor sits down.
 */
import { useEffect, useState } from 'react';

export const IDLE_MS = 10 * 60_000;

export function idleAfter(lastInputAt: number, now: number, limitMs: number): boolean {
  return now - lastInputAt >= limitMs;
}

const WAKE_EVENTS = ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel'] as const;

export function useIdle(limitMs: number, enabled = true): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (!enabled) {
      setIdle(false);
      return;
    }
    let last = Date.now();
    const wake = () => {
      last = Date.now();
      setIdle(false);
    };
    for (const e of WAKE_EVENTS) window.addEventListener(e, wake, { passive: true });
    // Coarse on purpose: a check every fifteen seconds is nothing, and the moment it fires is not
    // something he can perceive.
    const t = setInterval(() => {
      if (idleAfter(last, Date.now(), limitMs)) setIdle(true);
    }, 15_000);
    return () => {
      clearInterval(t);
      for (const e of WAKE_EVENTS) window.removeEventListener(e, wake);
    };
  }, [limitMs, enabled]);
  return idle;
}
